/**
 * Turn an array of strings into a class name, omitting empty values.
 *
 * @param {string|array...} a - An array of class names. Multidimensional arrays will be flattened,
 *   so both of these syntaxes are acceptable:
 *   makeClassName(['a', 'b'])
 *   makeClassName('a', 'b')
 *
 * @return {string}
 */
export const makeClassName = (...a) => flattenArray(a).filter(c => c).join(' ');

// Create a pair of functions to generate and reset a sequence of unique integer IDs.
// Exported and documented below as uniqueId and testResetUnitId.
const uniqueIdOperators = (() => {
  let nextId = 0;
  return [() => nextId++, (nextVal = 0) => nextId = nextVal];
})();

/**
 * Generate a numeric id which is unique to the page load.
 * @returns {int}
 */
export const uniqueId = uniqueIdOperators[0];

/**
 * Reset the uniqueId function to return the specified value.
 *
 * NOTE: this is currently ONLY intended for use in tests, to ensure stable identifiers for snapshot
 * tests, etc.  In future, it may be also useful for ensuring that client-rendered React IDs match
 * server-rendered content, but even then it should be used with caution, since employing it
 * incorrectly could result in accessibility problems due to non-unique IDs, broken React
 * optimizations due to non-unique keys, etc.
 *
 * @function testResetUniqueId
 * @param {number=0} nextVal - value to return the next time uniqueId is called (defaults to 0)
 */
export const testResetUniqueId = uniqueIdOperators[1];

/**
 * Recursively flatten an array.
 *
 * @param {Array} list - An array, possibly containing other arrays.
 * @returns {Array}
 */
export const flattenArray = list => list.reduce(
  (result, item) => result.concat(Array.isArray(item) ? flattenArray(item) : item), []
);

/**
 * Returns true if the badge is expired.
 *
 * @returns {boolean}
 */
export const isExpired = date => date && Date.now() > new Date(date).getTime();
